/**
 * Which themes this app offers — the brand's answer, not the model's.
 *
 * `lib/brand.ts` resolves `themes.allow` as a filter over *ids*, because it may
 * import only types from `@qufolio/model` (the build's config loads it through
 * Node, where that package does not resolve). This is the module that turns
 * those ids into `Theme`s, and it is the only one that needs both halves.
 *
 * ## Three sources, one list, and they are not interchangeable
 *
 * | | belongs to | a brand may |
 * |---|---|---|
 * | **built-in** | the product | choose which to offer |
 * | **brand** | this app | ship its own |
 * | **authored** | the person | nothing — see below |
 *
 * **A brand can never remove an authored theme.** Those are stored per user in
 * the workspace store, they are somebody's own work, and an app that dropped
 * them from the picker because of a line in a profile would be destroying
 * something it did not create. So `allow` filters the built-ins and nothing
 * else, and authored themes are appended whatever the brand says.
 *
 * ## Filtering the picker is not invalidating a workspace
 *
 * `allow` decides what the editor *lists*. It does not decide what resolves: a
 * workspace whose `themeRef` names a theme this brand no longer offers still
 * opens in it, because `ThemeProvider` searches the full set. Silently
 * repainting somebody's saved workspace is a worse failure than listing one
 * theme fewer than they remember — and a user who switches away from it simply
 * cannot switch back, which is the honest consequence of a brand narrowing its
 * palette.
 */

import { BUILTIN_THEMES, DEFAULT_THEME, type Theme } from "@qufolio/model";

import { BRAND } from "./brand-current";

/**
 * The built-ins this brand offers, in shipped order.
 *
 * `allow: null` (absent in the profile) means all of them; `[]` means none.
 * The two are different answers and both are legal, so this cannot collapse
 * them with a `??`.
 */
export function allowedBuiltins(): readonly Theme[] {
  const allow = BRAND.themes.allow;
  if (allow === null) return BUILTIN_THEMES;
  return BUILTIN_THEMES.filter((theme) => allow.includes(theme.id));
}

/**
 * Every theme a user of this app may pick, in the order the picker lists them.
 *
 * Brand themes come **before** the built-ins: a brand that ships a palette
 * means it to be the app's face, and burying it under six shipped ones would
 * make the feature pointless. Authored themes come last, where the user's own
 * work is next to the New/Duplicate controls that made it.
 *
 * An authored theme with the same id as a shipped one *replaces* it, which is
 * the rule `ThemeEditor` already applies — editing a copy of a built-in is how
 * an authored theme comes to exist, and two entries under one id would be a
 * picker with a duplicate that cannot be told apart.
 */
export function themeCatalogue(authored: readonly Theme[] = []): Theme[] {
  const overridden = new Set(authored.map((t) => t.id));
  const brandThemes = BRAND.themes.custom.filter((t) => !overridden.has(t.id));
  const builtins = allowedBuiltins().filter(
    (t) => !overridden.has(t.id) && !brandThemes.some((b) => b.id === t.id),
  );
  return [...brandThemes, ...builtins, ...authored];
}

/**
 * What a workspace naming no theme opens in.
 *
 * The brand's `themes.default` if it names something this app has, else the
 * product default. **Falling back rather than failing** is the same bargain
 * `resolveBrand` makes everywhere: by the time this runs the build has shipped,
 * and a typo in a theme id must not be a blank shell. `checkProfile` is where
 * that typo is caught, loudly, while somebody can still fix it.
 */
export function brandDefaultTheme(authored: readonly Theme[] = []): Theme {
  const wanted = BRAND.themes.defaultRef;
  if (!wanted) return DEFAULT_THEME;
  return themeCatalogue(authored).find((t) => t.id === wanted) ?? DEFAULT_THEME;
}
